/*
  Runtime Hot Loader: load JS/TS code from a string (ESM or CJS) in Node or Browser,
  with very verbose logging and optional hooks to resolve requires/imports.
*/
import type { System } from '../base/System.js';
import { resolveSystemEntry, type Process } from '../base/Process.js';

/**
 * Logger function used by HotLoader.
 */
export type LoggerFn = (message: string, extra?: any) => void;

/**
 * Custom resolver for CJS require calls.
 */
export type RequireResolver = (id: string) => any | undefined;

/**
 * Options for loading code from a string.
 */
export type LoadModuleOptions = {
  format?: 'auto' | 'esm' | 'cjs';
  ts?: 'auto' | boolean; // auto tries TS transpilation heuristics
  filename?: string; // for sourceURL hints
  requireResolver?: RequireResolver; // for CJS requires (browser-safe if provided)
  globals?: Record<string, any>; // injected globals for CJS wrapper and window/globalThis for ESM
  logger?: LoggerFn; // highly verbose logger
};

/**
 * Options for hot-reloading a system from source.
 */
export type HotReloadOptions = LoadModuleOptions & {
  exportName?: string; // name of the export to use if not default
  instantiate?: 'ctor' | 'factory'; // default: 'ctor'
};

const isNode = typeof process !== 'undefined' && !!(process as any).versions?.node;
const isBrowser = typeof globalThis !== 'undefined' && typeof (globalThis as any).window !== 'undefined' && typeof (globalThis as any).document !== 'undefined';

function defaultLogger(msg: string, extra?: any) { try { console.info(`[HotLoader] ${msg}`, extra ?? ''); } catch {} }

function looksLikeCJS(code: string) {
  return /(module\.exports\s*=|exports\.|require\s*\()/m.test(code);
}

function looksLikeESM(code: string) {
  return /(export\s+|import\s+)/m.test(code);
}

function looksLikeTS(code: string) {
  // Heuristics: type/interface/enum/import type/ generics annotations
  return /(interface\s+\w+|type\s+\w+\s*=|enum\s+\w+|import\s+type\s+|:\s*\w+\s*[=,){}]|<\w+\s*(extends|,|>)|implements\s+\w+)/m.test(code);
}

/**
 * The consumer's own `typescript` — an optional peer of @gotti/core, which ships no compiler. Imported at call time
 * through a specifier held in a variable, so no bundler resolves it statically: core's build does not inline it, and a
 * consumer's bundle neither pulls it in nor fails without it. Rejects naming the peer when it cannot be loaded (a
 * browser page has none), with the import failure as `cause`.
 */
export async function loadTypeScript(): Promise<any> {
  const spec = 'typescript';
  try {
    return await import(/* @vite-ignore */ spec);
  } catch (cause) {
    throw new Error('@gotti/core needs the optional peer "typescript" to hot-load systems: npm install -D typescript', { cause });
  }
}

function transpileTS(ts: any, source: string, logger: LoggerFn): string {
  logger('Using TypeScript.transpileModule for TS source');
  const out = ts.transpileModule(source, {
    compilerOptions: {
      target: ts.ScriptTarget.ES2020,
      module: ts.ModuleKind.ESNext,
      sourceMap: false,
      jsx: ts.JsxEmit.Preserve,
      esModuleInterop: true,
      moduleResolution: ts.ModuleResolutionKind.NodeJs,
      allowJs: true,
      isolatedModules: true,
      skipLibCheck: true,
      removeComments: false,
    }
  });
  return out.outputText as string;
}

/** The fallback without a compiler: an extremely naive strip of obvious TS syntax, best effort only. */
function stripTS(source: string): string {
  const code = source
    .replace(/\bexport\s+type\s+[^;]+;?/g, '')
    .replace(/\btype\s+[^;]+;?/g, '')
    .replace(/\binterface\s+[\s\S]*?\{/g, (m) => m.split('{')[0]) // keep the word, drop body below
    .replace(/\binterface\s+[\s\S]*?\}/g, '')
    .replace(/:\s*[^=,)>{\n]+/g, '') // : Type
    .replace(/<[^>]+>\s*(?=\()/g, '') // generic call sigs like fn<T>()
    .replace(/\bas\s+[^\s)]+/g, '') // as casts
    .replace(/\bimplements\s+[^\{]+/g, '') // implements Foo, Bar
    ;
  return code;
}

function withSourceURL(code: string, filename?: string) {
  if (!filename) return code;
  const hasSourceURL = code.includes('//# sourceURL=') || code.includes('//@ sourceURL=');
  return hasSourceURL ? code : `${code}\n//# sourceURL=${filename}`;
}

function getDataUrlForCode(code: string) {
  if (isNode) {
    // Node can import data URLs as ESM
    const b64 = Buffer.from(code, 'utf8').toString('base64');
    return `data:text/javascript;base64,${b64}`;
  } else {
    // Browser: we’ll use Blob URLs in loadESM
    return `data:text/javascript;charset=utf-8,${encodeURIComponent(code)}`;
  }
}

async function loadESMFromString(code: string, filename: string | undefined, logger: LoggerFn): Promise<any> {
  const codeWithHint = withSourceURL(code, filename);
  if (isBrowser) {
    const blob = new Blob([codeWithHint], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    logger(`Loading ESM via blob URL: ${filename ?? '<blob>'}`);
    try {
      const mod = await import(/* @vite-ignore */ url);
      return mod;
    } finally {
      try { URL.revokeObjectURL(url); } catch {}
    }
  } else {
    const url = getDataUrlForCode(codeWithHint);
    logger(`Loading ESM via data URL: ${filename ?? '<data-url>'}`);
    const mod = await import(/* @vite-ignore */ url);
    return mod;
  }
}

async function loadCJSFromString(code: string, filename: string | undefined, resolver: RequireResolver | undefined, globals: Record<string, any>, logger: LoggerFn): Promise<any> {
  return (await compileCJSFromString(code, filename, resolver, globals, logger))();
}

/**
 * Compile a CommonJS source into the function that runs it. Compiling parses it (a SyntaxError here means none of it
 * ran); calling the result runs it and returns its `module.exports`.
 */
async function compileCJSFromString(code: string, filename: string | undefined, resolver: RequireResolver | undefined, globals: Record<string, any>, logger: LoggerFn): Promise<() => any> {
  // CommonJS wrapper in both Node and Browser, with optional resolver
  const wrapped = withSourceURL(code, filename);
  const module = { exports: {} as any };
  const exportsObj = module.exports;
  let nodeRequire: any = undefined;
  if (isNode) {
    try {
      // createRequire only in Node
      const mod = await import(/* @vite-ignore */ 'module');
      const createReq = (mod as any).createRequire ?? (mod as any).default?.createRequire;
      if (createReq) nodeRequire = createReq(import.meta.url);
    } catch (e) {
      logger('Failed to create Node require, will use resolver only', e);
    }
  }
  const requireFn = (id: string) => {
    if (resolver) {
      const resolved = resolver(id);
      if (resolved !== undefined) return resolved;
    }
    if (nodeRequire) return nodeRequire(id);
    throw new Error(`require('${id}') is not supported in this context without a resolver.`);
  };
  const extraGlobalNames = Object.keys(globals || {});
  const argNames = ['module', 'exports', 'require', 'global', ...extraGlobalNames];
  const argValues = [module as any, exportsObj, requireFn, globals as any, ...extraGlobalNames.map(k => (globals as any)[k])];
  const fn = new Function(...argNames, wrapped) as (...args: any[]) => void;
  return () => {
    logger(`Evaluating CJS module${filename ? ' ' + filename : ''}`);
    fn(...argValues);
    return module.exports;
  };
}

/** How `loadModuleFromString` loads one candidate text of a source. */
type LoadTarget = {
  format: LoadModuleOptions['format'];
  filename: string;
  requireResolver: RequireResolver | undefined;
  globals: Record<string, any>;
  logger: LoggerFn;
};

function formatOf(code: string, target: LoadTarget): 'esm' | 'cjs' {
  if (target.format && target.format !== 'auto') return target.format;
  const format = looksLikeCJS(code) ? 'cjs' : (looksLikeESM(code) ? 'esm' : 'esm');
  target.logger(`Auto-detected format: ${format}`);
  return format;
}

async function loadCode(code: string, target: LoadTarget): Promise<any> {
  return formatOf(code, target) === 'esm'
    ? await loadESMFromString(code, target.filename, target.logger)
    : await loadCJSFromString(code, target.filename, target.requireResolver, target.globals, target.logger);
}

/** A parse failure is a SyntaxError — by name as well as by class, since another realm throws its own SyntaxError. */
const isSyntaxError = (error: unknown): boolean =>
  error instanceof SyntaxError || (error as { name?: unknown } | null | undefined)?.name === 'SyntaxError';

let ranMarkers = 0;

/**
 * Load `code` through the same path as `loadCode`, but tell a text that failed BEFORE any of it ran — it did not parse,
 * or (a module) did not link — from one that threw while running: the first resolves `{ unparsable: <its error> }`, the
 * second throws. Only the first may be retried with other text; code that started running never runs twice. A script
 * (CommonJS) is compiled before it runs, which separates the two. A module parses, links and runs inside one `import()`,
 * and a SyntaxError can come from running too (`JSON.parse`), so its first statement records that it started — on the
 * text's first line (after a hashbang), so every line number in a stack trace stays the source's own.
 */
async function loadUnlessUnparsable(code: string, target: LoadTarget): Promise<{ module: any } | { unparsable: unknown }> {
  if (formatOf(code, target) === 'cjs') {
    let run: () => any;
    try {
      run = await compileCJSFromString(code, target.filename, target.requireResolver, target.globals, target.logger);
    } catch (error) {
      if (isSyntaxError(error)) return { unparsable: error };
      throw error;
    }
    return { module: run() };
  }
  const key = `__gotti_hot_loader_ran_${++ranMarkers}__`;
  const g = globalThis as Record<string, unknown>;
  const hashbang = /^#![^\n]*\n?/.exec(code)?.[0] ?? '';
  const marked = `${hashbang}globalThis[${JSON.stringify(key)}] = true; ${code.slice(hashbang.length)}`;
  try {
    return { module: await loadESMFromString(marked, target.filename, target.logger) };
  } catch (error) {
    if (g[key] === true || !isSyntaxError(error)) throw error;
    return { unparsable: error };
  } finally {
    delete g[key];
  }
}

/**
 * Load a JS/TS module from a string.
 */
export async function loadModuleFromString(source: string, opts: LoadModuleOptions = {}): Promise<any> {
  const logger = opts.logger ?? defaultLogger;
  const filename = opts.filename ?? (opts.format === 'cjs' ? 'hot.cjs.js' : 'hot.esm.js');
  logger('Starting loadModuleFromString', { isNode, isBrowser, filename });
  const target: LoadTarget = { format: opts.format, filename, requireResolver: opts.requireResolver, globals: { ...(opts.globals ?? {}), globalThis }, logger };

  const tsMode: 'auto' | boolean = opts.ts ?? 'auto';
  if (tsMode !== true && !(tsMode === 'auto' && looksLikeTS(source))) return await loadCode(source, target);

  logger('TS detected (or forced) — attempting to transpile');
  let transpiled: string | undefined;
  try {
    transpiled = transpileTS(await loadTypeScript(), source, logger);
  } catch (e) {
    logger('TypeScript not available — no compiler to transpile with', e);
  }
  if (transpiled !== undefined) return await loadCode(transpiled, target);

  // No compiler: always so in a browser. `ts: true` declares the source TypeScript, so it is stripped.
  if (tsMode === true) {
    logger('Declared TypeScript (ts: true): naive TS stripping (best-effort only)');
    return await loadCode(stripTS(source), target);
  }
  // Otherwise only the heuristic says TypeScript, and it takes plenty of JavaScript for TypeScript (`{ x: 1 }`,
  // `a ? 1 : 2)`, a bundler's `{ get: all[name], enumerable: true }`) — which the strip breaks. So the source loads as
  // written first, and is stripped only when it does not even parse: every TypeScript-only syntax that cannot be
  // JavaScript (annotations, `interface`, `type X =`, `enum`, `import type`, `implements`, `as`, …) gets there through
  // that parse failure, so no textual signal for them is needed — and one would also fire on JavaScript that merely
  // mentions them in a comment or a string, breaking it. A runtime error in the source itself is never retried (that
  // code ran). A SyntaxError thrown while one of its static imports evaluates reads as a parse failure (the "started"
  // marker is the source's first statement, and imports evaluate before it): the strip is tried — nothing runs twice,
  // and that import's own error usually surfaces.
  // Residual: TypeScript whose only TypeScript syntax also parses as JavaScript runs as that JavaScript — a call's
  // explicit type arguments, `make<Player>(7)`, run as two comparisons. Pass `ts: true`, precompile, or install the peer.
  const asWritten = await loadUnlessUnparsable(source, target);
  if ('module' in asWritten) {
    logger('No compiler; the source parses as JavaScript — loaded as written');
    return asWritten.module;
  }
  logger('No compiler, and the source does not parse as JavaScript — naive TS stripping (best-effort only)', asWritten.unparsable);
  const stripped = await loadUnlessUnparsable(stripTS(source), target);
  if ('module' in stripped) return stripped.module;
  // The strip did not make it parse either: the source's own parse error says what is wrong with it.
  logger('The stripped source does not parse either', stripped.unparsable);
  throw asWritten.unparsable;
}

/**
 * Hot-reload a system definition from source code.
 */
export async function hotReloadSystemFromCode(
  process: Process,
  code: string,
  options: HotReloadOptions = {}
): Promise<System> {
  const logger = options.logger ?? defaultLogger;
  logger('Hot reloading system from code...', { format: options.format, ts: options.ts, exportName: options.exportName, instantiate: options.instantiate });

  // Provide helpful defaults so inline code can refer to global System
  const globals: Record<string, any> = {
    ...(options.globals ?? {}),
  };
  // If not provided, try to inject System into CJS globals for convenience
  if (!('System' in globals)) {
    try {
      const mod = await import('../base/System.js');
      (globals as any).System = (mod as any).System;
      logger('Injected System into globals for CJS convenience');
    } catch { /* ignore */ }
  }

  let restoreSystemGlobal = false;
  let prevSystemGlobal: any = undefined;
  // For ESM code that references `System` identifier, ensure it's available via a small prelude
  let preparedCode = code;
  if (!('System' in globals)) {
    try {
      const mod = await import('../base/System.js');
      (globals as any).System = (mod as any).System;
      logger('Injected System into globals for CJS convenience');
    } catch { /* ignore */ }
  }
  if ((options.format ?? 'auto') !== 'cjs') {
    // Provide System/global binding for ESM code
    if ((globalThis as any).System === undefined && (globals as any).System) {
      prevSystemGlobal = (globalThis as any).System;
      (globalThis as any).System = (globals as any).System;
      restoreSystemGlobal = true;
    }
    preparedCode = `const System = globalThis.System; const global = globalThis;\n` + code;
  }

  const module = await loadModuleFromString(preparedCode, { ...options, globals });
  if (restoreSystemGlobal) {
    try { (globalThis as any).System = prevSystemGlobal; } catch {}
  }
  logger('Module loaded; selecting export');

  const selected = (options.exportName ? module?.[options.exportName] : (module?.default ?? module)) as any;
  if (!selected) {
    throw new Error(`No export found${options.exportName ? ' named ' + options.exportName : ' (default)'} in hotloaded module.`);
  }

  const instantiateMode = options.instantiate ?? 'ctor';
  let systemCtorOrInstance: any = selected;
  if (instantiateMode === 'factory' && typeof selected === 'function') {
    logger('Instantiating system via factory function');
    systemCtorOrInstance = await selected();
  }

  // Name the system WITHOUT a throwaway construction: a class the process
  // already ran is named from its constructor map; otherwise the one instance
  // built to learn the name is the instance applied.
  const { name: sysName, entry } = resolveSystemEntry(systemCtorOrInstance, process);
  logger('Calling process.restartOrStartSystem', { system: sysName });
  const system = process.restartOrStartSystem(entry);
  logger('Hot reload complete', { system: sysName });
  return system;
}

// ── Many systems from one module ─────────────────────────────────────────────

/** How `applySystemModule` treats the systems a module exports. */
export type ApplySystemModuleOptions = {
  /**
   * Only these systems (by name). A listed system that is not running is
   * started; a listed name the module does not export is an error. Unset: every
   * exported system the process RUNS is restarted, the rest are skipped (a
   * shared module may export systems of areas this process is not in).
   */
  systemNames?: Array<string | number>;
  /**
   * `'restart'` (default): hot-swap running systems as above. `'add'`: start
   * systems that are NOT running (a running one is an error — restart it).
   */
  mode?: 'restart' | 'add';
  /** Owner recorded for systems this starts (see `Process.systemOwners`). */
  owner?: string;
  /** Systems never touched even when exported (reported as skipped) — e.g. the receiver applying the module. */
  exclude?: Array<string | number>;
};

/** What `applySystemModule` did, per system name. */
export type ApplySystemModuleResult = {
  /** Restarted (hot-swapped) running systems. */
  applied: string[];
  /** Systems that were not running and were started. */
  started: string[];
  /** Exported but not selected (not running, not named). */
  skipped: string[];
  /** One line per failure; a failed system was rolled back by `restartOrStartSystem`. */
  errors: string[];
};

const errText = (e: unknown) => (e instanceof Error ? `${e.name}: ${e.message}` : String(e));

/** Every distinct System class or instance a module exports (CJS `module.exports`, ESM namespace, or a bare class). */
export function exportedSystems(mod: unknown, SystemBase: Function): Array<{ exportName: string; value: any }> {
  const entries: Array<[string, unknown]> = typeof mod === 'function'
    ? [['module.exports', mod]]
    : mod && typeof mod === 'object' ? Object.entries(mod as Record<string, unknown>) : [];
  const seen = new Set<unknown>();
  const out: Array<{ exportName: string; value: any }> = [];
  for (const [exportName, value] of entries) {
    if (seen.has(value)) continue;
    const isClass = typeof value === 'function' && (value as Function).prototype instanceof SystemBase;
    const isInstance = !!value && typeof value === 'object' && value instanceof SystemBase;
    if (!isClass && !isInstance) continue;
    seen.add(value);
    out.push({ exportName, value });
  }
  return out;
}

/**
 * Apply the systems an evaluated module exports to a running process — the
 * generalized form of `hotReloadSystemFromCode` for a module that defines MANY
 * systems (a client systems file, a bundle). Each system is named with
 * `resolveSystemEntry` (no construction when the process already knows its
 * class); each is applied on its own — one that throws is rolled back and
 * reported, the rest still apply. Never throws for a system failure.
 */
export async function applySystemModule(process: Process, mod: unknown, opts: ApplySystemModuleOptions = {}): Promise<ApplySystemModuleResult> {
  const { System: SystemBase } = await import('../base/System.js');
  const result: ApplySystemModuleResult = { applied: [], started: [], skipped: [], errors: [] };
  const byName = new Map<string, any>();
  for (const { exportName, value } of exportedSystems(mod, SystemBase)) {
    let resolved;
    try {
      resolved = resolveSystemEntry(value, process);
    } catch (e) {
      result.errors.push(`construct ${exportName}: ${errText(e)}`);
      continue;
    }
    const name = String(resolved.name);
    if (byName.has(name)) {
      result.errors.push(`two exports define system '${name}' (${exportName} is ignored)`);
      continue;
    }
    byName.set(name, resolved.entry);
  }
  if (byName.size === 0 && result.errors.length === 0) result.errors.push('the module exports no System classes or instances');
  const wanted = opts.systemNames ? new Set(opts.systemNames.map(String)) : undefined;
  for (const name of wanted ?? []) {
    if (!byName.has(name)) result.errors.push(`system '${name}' is not exported by the module`);
    else if ((opts.exclude ?? []).map(String).includes(name)) result.errors.push(`system '${name}' cannot be applied here`);
  }
  const mode = opts.mode ?? 'restart';
  const excluded = new Set((opts.exclude ?? []).map(String));
  for (const [name, entry] of byName) {
    if (excluded.has(name)) {
      result.skipped.push(name);
      continue;
    }
    const running = name in process.systemLookup;
    const selected = wanted ? wanted.has(name) : (mode === 'add' ? !running : running);
    if (!selected) {
      result.skipped.push(name);
      continue;
    }
    try {
      if (mode === 'add') {
        if (running) throw new Error('already running — restart it instead');
        process.addSystemsArrayFromFile([entry], opts.owner);
        result.started.push(name);
      } else {
        process.restartOrStartSystem(entry, opts.owner);
        (running ? result.applied : result.started).push(name);
      }
    } catch (e) {
      result.errors.push(`${name}: ${errText(e)}`);
    }
  }
  return result;
}

/** `loadModuleFromString` + `applySystemModule`. An evaluation failure is reported in `errors`, not thrown. */
export async function hotReloadSystemsFromCode(
  process: Process,
  code: string,
  options: LoadModuleOptions & ApplySystemModuleOptions = {},
): Promise<ApplySystemModuleResult> {
  let mod: unknown;
  try {
    mod = await loadModuleFromString(code, options);
  } catch (e) {
    return { applied: [], started: [], skipped: [], errors: [`evaluate: ${errText(e)}`] };
  }
  return applySystemModule(process, mod, options);
}

// ── The server → client hot-code contract ───────────────────────────────────

/** System name a client's hot-code receiver registers under (`HotCodeSystem` in @gotti/client). */
export const HOT_CODE_SYSTEM = '__gotti_hot_code__';
/** Request type an area sends that receiver (`AreaServer.reloadClientSystems`). */
export const HOT_CODE_REQUEST = 'gotti:hot-code';

/**
 * What a server sends a client's hot-code receiver: `apply` evaluates `code`
 * (a CJS or ESM module with `@gotti/*` left as imports/requires — the client
 * resolves them to its own instances) and applies its systems; `remove`
 * removes `systemNames`.
 */
export type HotCodeRequest = {
  reloadId: string;
  op: 'apply' | 'remove';
  code?: string;
  systemNames?: Array<string | number>;
  mode?: 'restart' | 'add';
  /** Name shown in stack traces (`//# sourceURL`). */
  filename?: string;
};

/** A client's answer to a `HotCodeRequest`. `removed` is set for `op: 'remove'`. */
export type HotCodeResult = ApplySystemModuleResult & { reloadId: string; removed?: string[] };
